/**
 * One source file of an audit, read-only: its rows as locked into the audit,
 * its details, and the original download. Intake actions never appear on the
 * audit side — to change which files an audit uses, use Change data scope on
 * the audit's Data tab.
 */

import { useModule } from '../modules/useModule';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { Note, StatusBadge } from '../../shared/ui';
import { useAuditFileQuery } from '../../queries/intake';
import { ErrorState, LoadingState } from '../shared/states';
import { downloadFile } from './FileTable';
import { useAuditContext } from '../audits/AuditWorkspace';
import { PageHeader } from '../../provider/pageTitle';
import { FileDetailsDrawer } from './FileDetailsDrawer';
import { FileDataWorkbench, fileMetaLine } from './FileDataWorkbench';
import { date } from '../../utils/format';

export default function AuditFileDetail() {
	const { auditId = '', fileId = '' } = useParams();
	const [details, setDetails] = useState(false);
	const { audit, crumbs } = useAuditContext();
	const { link } = useModule();

	const file = useAuditFileQuery(auditId || null, fileId || null);
	const back = link(`/audits/${auditId}/data`);
	const download = useMutation({ mutationFn: downloadFile });

	if (file.isPending) return <LoadingState label="Loading the file…" />;
	if (file.error) {
		return (
			<ErrorState
				error={file.error}
				onRetry={file.refetch}
				hint={
					<>
						Every file this audit uses is listed on its{' '}
						<Link className="link" to={back}>
							Data tab
						</Link>
						.
					</>
				}
			/>
		);
	}
	if (!file.data) return null;
	const current = file.data;

	return (
		<div className="stack">
			<PageHeader
				breadcrumbs={[...crumbs, { label: 'Data', to: back }]}
				title={current.originalName}
				subtitle={fileMetaLine(current) || undefined}
				hint={<StatusBadge group="fileStatus" value={current.status} />}
				actions={
					<div
						className="row"
						style={{ gap: 'var(--s2)', flexWrap: 'wrap' }}
					>
						<Button
							variant="outlined"
							size="small"
							onClick={() => setDetails(true)}
						>
							File details
						</Button>
						<Button
							loading={download.isPending}
							variant="outlined"
							size="small"
							disabled={download.isPending}
							onClick={() => download.mutate(current.pkFileId)}
						>
							Download original file
						</Button>
					</div>
				}
			/>

			{Boolean(download.error) && (
				<Note tone="danger" title="Could not download the original" row>
					{download.error instanceof Error
						? download.error.message
						: 'Something failed.'}
				</Note>
			)}

			<FileDataWorkbench
				file={current}
				auditId={auditId}
				between={(summary) =>
					summary ? (
						<p className="small mut">
							{summary.source === 'audit'
								? `Rows in this audit’s scope${
										audit.scopeLockedAt
											? `, locked on ${date(audit.scopeLockedAt)}`
											: ''
									}. Later changes to the file don’t move this audit’s figures.`
								: 'This audit’s data scope isn’t locked yet — showing the file’s current rows.'}
						</p>
					) : null
				}
			/>

			{details && (
				<FileDetailsDrawer
					file={current}
					onClose={() => setDetails(false)}
				/>
			)}
		</div>
	);
}
